// =====================================================
// USOS DE LOTE / DESTINO DE ESTABLECIMIENTO
// BDSJEA-409: el catálogo (códigos y etiquetas) YA NO vive acá: lo sirve el
// backend (GET /api/agro/dominios/uso-lote, tabla TABLASGENERALES 146) y se
// lee con el hook hooks/useUsosLote. Acá quedan solo las constantes con
// lógica y los colores del mapa (no están en la tabla).
// Lo consumen EditorPoligonos, InformacionSatelitalLote y el
// alta/edición de Establecimientos (campo Destino).
// =====================================================

export const USO_AGRICOLA = "AGRICOLA"
export const USO_OTRO = "OTRO"

// Uso con el que se precarga el alta de un lote (EditorPoligonos)
export const USO_LOTE_DEFAULT = USO_AGRICOLA

// Color por código para los 11 destinos conocidos (CAMPOS: V0273, BDSJEA-458). Un código nuevo del
// catálogo usa COLOR_USO_DEFAULT (ver colorUso).
// BDSJEA-463 (paleta confirmada por el developer): Agricultura verde, Ganadería celeste, Tambo azul
// índigo; Campos y "sin uso" (OTRO / código desconocido) gris; Porcinos, Ovinos y Cedidos conservan
// su color.
export const COLOR_USO_DEFAULT = "#999999"
export const COLORES_USO = {
  AGRICOLA: "#00C49F",
  PASTURA: "#FFBB28",
  MONTE: "#82ca9d",
  RESERVA: "#8884d8",
  OTRO: COLOR_USO_DEFAULT,
  GANADERIA: "#4FC3F7",
  TAMBO: "#3F51B5",
  PORCINOS: "#E57373",
  OVINOS: "#A1887F",
  CEDIDOS: "#B0BEC5",
  CAMPOS: COLOR_USO_DEFAULT,
}

/** Color de un código de uso/destino; gris si no es uno de los conocidos. */
export const colorUso = codigo =>
  COLORES_USO[String(codigo || "").toUpperCase()] || COLOR_USO_DEFAULT

/**
 * Etiqueta visible de un código de uso/destino. `usos` es la lista del
 * catálogo ({ value, label }) que devuelve useUsosLote. Devuelve "-" si no
 * hay valor y el propio código si no está en el catálogo.
 */
export const labelUsoLote = (codigo, usos = []) => {
  if (!codigo) return "-"
  const uso = usos.find(u => u.value === String(codigo).toUpperCase())
  return uso ? uso.label : codigo
}

// =====================================================
// TENENCIA DEL LOTE (BDSJEA-463)
// Un lote tiene UNA sola tenencia (PROPIO | ARRENDADO; null = sin dato). El mapa colorea por
// destino y distingue la tenencia con una trama: propio = punteado, arrendado = rayado, sin dato =
// sin trama. La tenencia también se informa en texto (popup, lista, leyenda): no depende solo de
// la trama ni del color.
// =====================================================

export const TENENCIA_PROPIO = "PROPIO"
export const TENENCIA_ARRENDADO = "ARRENDADO"
export const PATRON_PUNTEADO = "punteado"
export const PATRON_RAYADO = "rayado"

// Opciones del select de tenencia (el catálogo son 2 códigos fijos de dom_tipo_tenencia)
export const TENENCIAS_LOTE = [
  { value: TENENCIA_PROPIO, label: "Propio" },
  { value: TENENCIA_ARRENDADO, label: "Arrendado" },
]

const PATRON_POR_TENENCIA = {
  [TENENCIA_PROPIO]: PATRON_PUNTEADO,
  [TENENCIA_ARRENDADO]: PATRON_RAYADO,
}

const normalizarTenencia = tenencia => String(tenencia || "").toUpperCase()

/** Trama de una tenencia: "punteado" | "rayado" | null (sin dato o desconocida). */
export const patronTenencia = tenencia =>
  PATRON_POR_TENENCIA[normalizarTenencia(tenencia)] || null

/** Etiqueta visible de una tenencia: "Propio", "Arrendado" o "Sin dato". */
export const etiquetaTenencia = tenencia => {
  const t = TENENCIAS_LOTE.find(x => x.value === normalizarTenencia(tenencia))
  return t ? t.label : "Sin dato"
}

/** Id del <pattern> SVG de una trama y un color (ver components/Mapa/PatronesLote). */
export const idPatronLote = (patron, color) =>
  `lote-trama-${patron}-${String(color).replace("#", "").toLowerCase()}`

// Estilo del lote por defecto (generado automáticamente): gris con borde discontinuo, como antes.
const COLOR_BORDE_DEFAULT = "#595959"
const COLOR_RELLENO_DEFAULT = "#cccccc"
const OPACIDAD_SIN_TRAMA = 0.3
// BDSJEA-471: trazo del borde de un polígono aproximado (generado por la importación)
export const DASH_APROXIMADO = "2, 6"
export const LEYENDA_POLIGONO_APROXIMADO =
  "Polígono aproximado (generado por la importación, ubicación y forma estimadas)"
// Con trama la opacidad del relleno la define el propio <pattern> (fondo tenue + motivo marcado).
const OPACIDAD_CON_TRAMA = 1

/**
 * Estilo de un lote en el mapa: color por destino + trama por tenencia.
 * Devuelve { color, fillColor, fillOpacity, weight, dashArray, patron, patternId, etiquetaTenencia }:
 *  - color: borde (color del destino); fillColor: color del destino, o "url(#patternId)" si hay trama
 *    (react-leaflet pasa fillColor tal cual al atributo `fill` del <path> SVG);
 *  - patron: "punteado" | "rayado" | null; patternId: id del <pattern> o null;
 *  - esDefault: lote generado automáticamente (gris, borde discontinuo, sin trama).
 *  - BDSJEA-471, aproximado: polígono generado por la importación (ubicación y forma estimadas).
 *    CONSERVA color y trama y solo cambia el borde a trazo discontinuo y más grueso; devuelve
 *    aproximado=true. esDefault tiene prioridad (gris).
 */
export const estiloLote = ({
  usoActual,
  tipoTenencia,
  esDefault,
  aproximado,
} = {}) => {
  const tenencia = etiquetaTenencia(tipoTenencia)
  if (esDefault) {
    return {
      color: COLOR_BORDE_DEFAULT,
      fillColor: COLOR_RELLENO_DEFAULT,
      fillOpacity: OPACIDAD_SIN_TRAMA,
      weight: 4,
      dashArray: "8, 6",
      patron: null,
      patternId: null,
      aproximado: false,
      etiquetaTenencia: tenencia,
    }
  }
  const esAproximado = aproximado === true
  const color = colorUso(usoActual)
  const patron = patronTenencia(tipoTenencia)
  const patternId = patron ? idPatronLote(patron, color) : null
  return {
    color,
    fillColor: patron ? `url(#${patternId})` : color,
    fillOpacity: patron ? OPACIDAD_CON_TRAMA : OPACIDAD_SIN_TRAMA,
    weight: esAproximado ? 3 : 2,
    dashArray: esAproximado ? DASH_APROXIMADO : null,
    patron,
    patternId,
    aproximado: esAproximado,
    etiquetaTenencia: tenencia,
  }
}

/** Colores distintos de la paleta (para generar los <pattern> del mapa). */
export const coloresDeLaPaleta = () => [
  ...new Set([...Object.values(COLORES_USO), COLOR_USO_DEFAULT]),
]
